import { useCallback } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAccount } from '@/hooks/useAccount';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { EventName, PageName } from '@/shared/types/segment';

const createAccountSchema = yup.object({
  name: yup.string().required(() => t('accounts.name_required')),
  slug: yup
    .string()
    .min(3, () => t('accounts.slug_too_short'))
    .required(),
});

type CreateAccountFormValues = yup.InferType<typeof createAccountSchema>;

export const CreateAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createAccount, checkSlugAsync } = useAccount();

  useSegments(PageName.CREATE_ACCOUNT);

  const form = useForm<CreateAccountFormValues>({
    resolver: yupResolver<CreateAccountFormValues, unknown, CreateAccountFormValues>(
      createAccountSchema
    ),
    defaultValues: { name: '', slug: '' },
    mode: 'onBlur',
  });

  const { control, setValue, setError, clearErrors, formState } = form;
  const nameValue = useWatch({ control, name: 'name' });
  const slugValue = useWatch({ control, name: 'slug' });

  const generateSlug = useCallback((name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  }, []);

  const validateSlug = useCallback(
    async (slug: string) => {
      if (slug.length < 3) {
        setError('slug', { message: t('accounts.slug_too_short') });
        return;
      }

      const isAvailable = await checkSlugAsync(slug);
      if (!isAvailable) {
        setError('slug', { message: t('accounts.slug_taken') });
      } else {
        clearErrors('slug');
      }
    },
    [checkSlugAsync, setError, clearErrors, t]
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setValue('name', value);
      const newSlug = generateSlug(value);
      setValue('slug', newSlug);
      if (newSlug) {
        validateSlug(newSlug);
      } else {
        clearErrors('slug');
      }
    },
    [generateSlug, setValue, clearErrors, validateSlug]
  );

  const handleSlugChange = useCallback(
    (value: string) => {
      const slug = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setValue('slug', slug);
      validateSlug(slug);
    },
    [setValue, validateSlug]
  );

  const onSubmit = useCallback(
    async (values: CreateAccountFormValues) => {
      trackEvent(EventName.ClickCreateAccount);

      createAccount(
        { name: values.name, slug: values.slug },
        {
          onSuccess: (newAccount) => {
            navigate(`/${newAccount.slug}`);
          },
        }
      );
    },
    [createAccount, navigate]
  );

  const isSubmitting = formState.isSubmitting;
  const hasSlugError = !!formState.errors.slug;

  return (
    <div className="flex h-screen justify-center overflow-y-auto bg-sidebar">
      <div className="flex w-full flex-col items-center gap-6 px-4 pt-12 pb-24">
        <div className="text-center">
          <h1 className="text-2xl leading-8 font-semibold tracking-tight">
            {t('accounts.create_account')}
          </h1>
          <p className="text-base text-muted-foreground">{t('accounts.create_account_subtitle')}</p>
        </div>

        <div className="w-[448px] max-w-full animate-fade-in-up-blur rounded-lg border bg-background p-6 pb-7">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-9"
              aria-label={t('accounts.create_account_form')}
            >
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>{t('accounts.account_name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder={t('accounts.account_name_placeholder')}
                          disabled={isSubmitting}
                          autoFocus
                          onChange={(e) => handleNameChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>{t('accounts.account_slug')}</FormLabel>
                      <FormControl>
                        <div className="flex h-10 w-full items-center overflow-hidden rounded-md border border-border bg-background px-3 py-2.5">
                          <span className="shrink-0 text-sm text-muted-foreground">
                            {window.location.host}/
                          </span>
                          <input
                            {...field}
                            type="text"
                            onChange={(e) => handleSlugChange(e.target.value)}
                            placeholder={t('accounts.slug_placeholder')}
                            disabled={isSubmitting}
                            className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                disabled={!nameValue || !slugValue || hasSlugError}
                className="w-full"
                isLoading={isSubmitting}
              >
                {t('accounts.create_account')}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
